import { z } from 'zod'
import { Section, fields } from './definitie.js'
import { DefinitieId, InstantieId, PublicatieId, RuleId, SectionId, FieldId } from './id.js'
import { Side, Role } from './identity.js'
import { Laag } from './laag.js'
import { Person } from './moment.js'
import { Text } from './text.js'

/**
 * De projectie is wat de client krijgt: een per-gebruiker gefilterde vorm, nooit
 * de volledige definitie (R-01a, ontwerp 5.2).
 *
 * Ze draagt de vorm, de rechten per sectie en de regelstatus, en geen waarden.
 * In de bewerkbare weergaven komen de waarden uit de sectiedocumenten waarmee de
 * client verbinding krijgt, en dat zijn precies de secties die in de projectie
 * staan (6.8). In de alleen-lezen weergaven komen ze uit de uitgeschreven stand
 * van de aangewezen revisie, langs dezelfde projectie gefilterd (4.5).
 *
 * Wie geen recht op een sectie heeft, krijgt die sectie niet: ze staat er niet
 * in. Dat is geen fout maar de projectie zelf (R-68, ontwerp 9.2 regel 1).
 *
 * De berekening zelf staat in `packages/engine` en op precies één plek, gebruikt
 * door elk pad dat leest of schrijft (M1, ontwerp 5.2). Een tweede implementatie
 * voor een ander kanaal is een fout en geen optimalisatie; in de PortaalPOC stond
 * dezelfde regel drie keer los en liepen het gewone verzoek en de live verbinding
 * uiteen (bijlage B, probleem 3).
 */

/** Wat iemand met een sectie mag (R-65). Geen recht betekent: de sectie ontbreekt. */
export const Permission = z.enum(['lezen', 'bewerken'])
export type Permission = z.infer<typeof Permission>

/**
 * Een geprojecteerde sectie is de sectievorm plus het recht van deze gebruiker, en haar nummer in de
 * definitie.
 *
 * **Het nummer komt uit de definitie en niet uit de projectie** (audit delen G-17, besluit van 2
 * november 2026). Een medeschrijver met twee van de zeven secties zag ze als 1 en 2, terwijl de
 * aanvrager ze 3 en 7 noemde; een gesprek over "sectie 3" liep zo mis. De plaats onder de secties van
 * dezelfde soort: een toetssectie telt onder de toetssecties, de rest onder de rest, zodat de invoer
 * bij invuller, medeschrijver en toetser hetzelfde nummer draagt. Een sectie die een regel verbergt
 * houdt haar plaats, dus dan valt er een nummer weg. Optioneel, want een projectie van vóór dit veld
 * (een test, het voorbeeld van een oudere configurator) heeft het niet; dan telt de pagina zelf.
 */
export const ProjectionSection = Section.extend({
  recht: Permission,
  nummer: z.number().int().positive().optional(),
  /**
   * Of deze lezer in deze sectie een opmerking mag beginnen of beantwoorden, naar de grond waarop hij
   * haar leest (R-73, R-184b, R-155f, R-66b; opmerkingen van november 2026). Onwaar als hij de sectie
   * alleen langs een opening door beheer of langs zijn adviesverzoek leest, ook als hij elders op het
   * dossier een eigen toekenning heeft. De pod rekent het uit met dezelfde berekening waarmee hij een
   * opmerking weigert (`getCommentGrounds` in `apps/runtime/src/projection.ts`); de renderer leidt het
   * niet zelf af. Over de soort sectie en de beurt zegt het niets: dat leest de renderer uit de sectie
   * en `actieveLaag`. Optioneel, zoals `nummer`: een projectie zonder dit veld (een test, het voorbeeld
   * in de configurator) laat de knop staan, en de pod weigert dan zoals altijd.
   */
  magOpmerken: z.boolean().optional(),
})
export type ProjectionSection = z.infer<typeof ProjectionSection>

export const Projection = z
  .strictObject({
    instantieId: InstantieId,
    publicatieId: PublicatieId,
    definitieId: DefinitieId,
    titel: Text,
    /** Of de vragen per sectie zijn genummerd, uit de definitie (R-04); afwezig is aan. */
    nummering: z.boolean().optional(),
    /** Welke kant aan zet is; de renderer leidt daaruit af wat bewerkbaar is (R-193). */
    actieveLaag: Laag,
    /**
     * Of het formulier na indienen wordt getoetst (`heeftToetslaag`, besluit van 30 oktober 2026).
     * De renderer zegt het de invuller in de bevestiging na indienen; hij leidt het niet zelf af uit
     * de secties, want een toetssectie valt buiten de projectie van een invuller. Optioneel zodat een
     * projectie van vóór die dag geldig blijft; ontbreekt het, dan zegt de bevestiging niets over een toets.
     */
    getoetst: z.boolean().optional(),
    /**
     * Wie er handelt (B18). De kant komt uit de ingang waarlangs iemand inlogde (R-155a) en
     * stuurt het delingsoverzicht (R-66a). De rol komt sinds 9 oktober 2026 uit de toekenningen
     * van het processysteem en niet meer uit het token: `toetser` als er een toekenning als
     * toetser is, anders `invuller`, ook voor wie alleen via een deling of een opening door
     * beheer binnenkwam. Ze wijst de laag aan waarin iemand schrijft (R-172b); wie als invuller
     * én als toetser is toegekend, schrijft in de laag die aan zet is (`ownLaag` in de engine).
     */
    kant: Side,
    rol: Role,
    /**
     * Of deze persoon op deze instantie mag delen (R-64, R-64d). De server
     * rekent het uit; de renderer leidt het niet zelf af, want dan staat de regel
     * twee keer. Welke secties deelbaar zijn volgt uit `secties[].recht`, en hoe
     * ver een deling mag gaan uit R-65a tot R-65c.
     */
    magDelen: z.boolean(),
    /**
     * Of deze persoon op deze instantie mag indienen (R-69): wie een toekenning als invuller
     * van het processysteem heeft, en niet wie alleen via een deling of een opening door beheer
     * binnenkwam. Of de beurt het nu toestaat is een tweede vraag, die de pod bij de overdracht
     * stelt.
     *
     * Deze drie velden zijn verplicht sinds de berekening van vóór het register uit de engine is
     * gehaald (opdracht 4b van de reeks toegang via toekenningen); tot dan waren ze optioneel,
     * omdat die oude berekening ze niet kende.
     */
    magIndienen: z.boolean(),
    /** Namens wie deze persoon handelt, uit zijn toekenning als gemachtigde (R-190, R-191); anders null. */
    namens: Person.nullable(),
    /**
     * Of deze opening alleen op een toekenning met bron beheer rust (R-155f, R-66b): zonder die
     * toekenning stond er geen enkele sectie in. De weergave laat dan zien dat er als beheerder
     * wordt gekeken.
     */
    beheer: z.boolean(),
    secties: z.array(ProjectionSection),
  })
  .superRefine((projection, ctx) => {
    const sectionIds = new Set(projection.secties.map((s) => s.id))
    const fieldIds = new Set(projection.secties.flatMap((s) => fields(s).map(({ veld: field }) => field.id)))

    projection.secties.forEach((section, s) => {
      section.regels.forEach((rule, r) => {
        const rulePath = (key: string) => ['secties', s, 'regels', r, key]
        // De hoofdregel uit ontwerp 5.2: een regel reist alleen mee naar de client
        // als al zijn invoer ook meereist. Zit het bronveld in een sectie die deze
        // gebruiker niet mag zien, dan blijft de regel op de server en gaat alleen
        // de uitkomst mee. Een projectie die dat niet aanhoudt lekt het bestaan van
        // een veld dat is weggefilterd.
        if (rule.soort === 'zichtbaarBij') {
          if (!fieldIds.has(rule.bronVeld)) {
            ctx.addIssue({
              code: 'custom',
              path: rulePath('bronVeld'),
              message: `bronVeld ${rule.bronVeld} zit niet in deze projectie; deze regel hoort op de server te blijven`,
            })
          }
          if (rule.doel !== section.id && !fieldIds.has(rule.doel)) {
            ctx.addIssue({ code: 'custom', path: rulePath('doel'), message: `doel ${rule.doel} zit niet in deze projectie` })
          }
        } else if (rule.soort === 'zichtbaarAls') {
          rule.voorwaarden.forEach((condition, c) => {
            if (!fieldIds.has(condition.bronVeld)) {
              ctx.addIssue({
                code: 'custom',
                path: ['secties', s, 'regels', r, 'voorwaarden', c, 'bronVeld'],
                message: `bronVeld ${condition.bronVeld} zit niet in deze projectie; deze regel hoort op de server te blijven`,
              })
            }
          })
          if (rule.doel !== section.id && !fieldIds.has(rule.doel)) {
            ctx.addIssue({ code: 'custom', path: rulePath('doel'), message: `doel ${rule.doel} zit niet in deze projectie` })
          }
        } else if (rule.soort === 'korteSectie') {
          if (!sectionIds.has(rule.doelSectie)) {
            ctx.addIssue({
              code: 'custom',
              path: rulePath('doelSectie'),
              message: `doelSectie ${rule.doelSectie} zit niet in deze projectie`,
            })
          }
        } else if (!fieldIds.has(rule.doelVeld)) {
          ctx.addIssue({
            code: 'custom',
            path: rulePath('doelVeld'),
            message: `doelVeld ${rule.doelVeld} zit niet in deze projectie`,
          })
        }
      })
    })
  })

export type Projection = z.infer<typeof Projection>

/**
 * De zeven weergavemodi (ontwerp 6.6, R-138a).
 *
 * Een weergave is een rendermodus over dezelfde definitie en dezelfde gefilterde
 * projectie, en geen kopie: een gedaante erbij is een modus erbij en geen tweede
 * formulier. Dit is een prop van de renderer en geen onderdeel van de projectie;
 * het type staat hier omdat het nergens anders thuishoort.
 *
 * Twee gedaanten uit eisenboek 8.17 zijn bewust geen modus: beoordelen (R-134)
 * is het invullen van een eigen beoordelingsformulier, en geaggregeerd (R-136)
 * gebeurt in een later bouwblok.
 */
export const ViewMode = z.enum([
  'invullen',
  'aanvullen',
  'nalezen',
  'geanonimiseerd',
  'met-historie',
  'intern',
  'toetsen',
])
export type ViewMode = z.infer<typeof ViewMode>

/** Eén falende regel, met de melding in beide talen (ontwerp 5.7). */
export const Finding = z.strictObject({ regelId: RuleId, melding: Text })
export type Finding = z.infer<typeof Finding>

/**
 * Wat de pod na een verwerkte wijziging terugstuurt, en tegelijk het formaat van
 * `GET /voortgang` (ontwerp 9.2).
 *
 * De melding reist in beide talen mee, zodat een taalwissel van de invuller
 * (R-144) geen nieuwe evaluatie vraagt en de client voor server-side geëvalueerde
 * regels niets van de regel zelf hoeft te weten (ontwerp 5.7).
 *
 * `hardeControles` per veld staat niet in het voorbeeld van 9.2; dat voorbeeld
 * toont een geldig veld en heeft de lijst dus niet nodig. Zonder die lijst kan de
 * renderer een falende harde controle niet aan zijn veld koppelen, en dat is wat
 * R-140 vraagt.
 */
export const EvaluationStatus = z.strictObject({
  veld: z.record(
    FieldId,
    z.strictObject({
      status: z.enum(['geldig', 'ongeldig']),
      hardeControles: z.array(Finding),
      waarschuwingen: z.array(Finding),
    }),
  ),
  sectie: z.record(
    SectionId,
    z.strictObject({
      status: z.enum(['volledig', 'onvolledig']),
      /**
       * Alle openstaande harde controles in deze sectie, op veldniveau en op
       * sectieniveau samen. Dat is het getal waarmee een sectiekop kan laten zien
       * hoeveel er nog te doen is, en het is ook wat 9.2 met dit veld bedoelt.
       */
      openstaandeHardeControles: z.number().int().nonnegative(),
      /**
       * Alleen de bevindingen van regels die de sectie zelf als onderwerp hebben,
       * dus `korteSectie` (B11).
       *
       * Zonder deze twee lijsten kan een bevinding op sectieniveau de invuller niet
       * bereiken: ze hoort bij geen enkel veld, dus de lijsten per veld hebben er
       * geen plek voor. Twee lijsten en niet één, want `korteSectie` is standaard
       * een waarschuwing maar de inrichter kan er een harde controle van maken
       * (D-15).
       */
      hardeControles: z.array(Finding),
      waarschuwingen: z.array(Finding),
    }),
  ),
  formulier: z.strictObject({
    /** Indienen kan niet zolang een harde controle faalt (R-23, R-31). */
    indienbaar: z.boolean(),
    openstaandeHardeControles: z.number().int().nonnegative(),
  }),
})
export type EvaluationStatus = z.infer<typeof EvaluationStatus>
